import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Loader2, Search, FileText, Package, TrendingUp, Eye, Users, BarChart3, Globe, MessageCircleQuestion, Mail, Target, Compass, Route, DoorOpen, Smartphone, Monitor, Tablet, ShoppingCart, CreditCard, UserPlus, Gift } from "lucide-react";

export default function AdminInsights() {
  const [data, setData] = useState(null);
  const [traffic, setTraffic] = useState(null);
  const [landing, setLanding] = useState(null);
  const [entry, setEntry] = useState(null);
  const [journeys, setJourneys] = useState(null);
  const [range, setRange] = useState("30d");
  const [camp, setCamp] = useState(null);

  useEffect(() => {
    api.get("/admin/insights").then((r) => setData(r.data)).catch(() => setData(false));
    api.get("/admin/traffic").then((r) => setTraffic(r.data)).catch(() => setTraffic(false));
    api.get("/admin/landing-funnel").then((r) => setLanding(r.data)).catch(() => setLanding(false));
    api.get("/admin/journeys").then((r) => setJourneys(r.data)).catch(() => setJourneys(false));
    // Campaign comparison — this week vs last week.
    const now = Date.now();
    const iso = (n) => new Date(now - n * 86400000).toISOString();
    Promise.all([
      api.get(`/admin/entry-pages?start=${encodeURIComponent(iso(7))}`),
      api.get(`/admin/entry-pages?start=${encodeURIComponent(iso(14))}&end=${encodeURIComponent(iso(7))}`),
    ])
      .then(([a, b]) => setCamp({ thisWeek: a.data.campaigns || [], lastWeek: b.data.campaigns || [] }))
      .catch(() => setCamp(false));
  }, []);

  useEffect(() => {
    const iso = (n) => new Date(Date.now() - n * 86400000).toISOString();
    const start = range === "all" ? "" : range === "7d" ? iso(7) : iso(30);
    const params = start ? `?start=${encodeURIComponent(start)}` : "";
    setEntry(null);
    api.get(`/admin/entry-pages${params}`).then((r) => setEntry(r.data)).catch(() => setEntry(false));
  }, [range]);

  if (!data) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-primary" /></div>;

  const RANGES = [["7d", "Last 7 days"], ["30d", "Last 30 days"], ["all", "All time"]];

  return (
    <div>
      <h1 className="font-heading font-black text-3xl tracking-tight">Insights</h1>
      <p className="text-muted-foreground mt-2">Visitor traffic plus what customers search, preview and buy.</p>

      <div className="flex items-center gap-2 mt-6" data-testid="insights-range">
        <span className="text-xs uppercase tracking-wider font-semibold text-muted-foreground mr-1">Entry & audience window:</span>
        {RANGES.map(([k, label]) => (
          <button
            key={k}
            onClick={() => setRange(k)}
            data-testid={`range-${k}`}
            className={`text-xs font-semibold px-3 py-1.5 border transition-colors ${range === k ? "bg-primary text-white border-primary" : "bg-white border-border hover:border-primary"}`}
          >
            {label}
          </button>
        ))}
      </div>

      {camp && camp.thisWeek && <CampaignCompare c={camp} />}

      {entry && entry.audience && <Audience d={entry} />}

      {traffic && <Traffic t={traffic} />}

      {entry && entry.pages && <EntryPages d={entry} />}

      {journeys && journeys.journeys && <Journeys d={journeys} />}

      {landing && landing.pages && <LandingFunnel d={landing} />}

      {data.assistant_total > 0 && (
        <div className="mt-6 bg-white border border-border p-6" data-testid="insights-assistant">
          <div className="flex items-center gap-2 mb-1">
            <MessageCircleQuestion className="w-4 h-4 text-primary" />
            <h3 className="font-heading font-bold text-sm">What visitors asked the Form Finder ({data.assistant_total})</h3>
          </div>
          <p className="text-xs text-muted-foreground mb-5">Real situations people typed — build or feature what they need.</p>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <div className="text-xs uppercase tracking-wider font-semibold text-muted-foreground mb-2">Recent questions</div>
              <div className="space-y-1.5">
                {data.assistant_recent.length === 0 ? <Empty>No questions yet.</Empty> :
                  data.assistant_recent.map((a, i) => (
                    <div key={i} className="flex items-start gap-2 text-sm border-b border-border py-1.5">
                      <span className="flex-1">{a.query}</span>
                      <span className={`text-xs font-semibold whitespace-nowrap ${a.result_count === 0 ? "text-amber-600" : "text-muted-foreground"}`}>
                        {a.result_count} {a.result_count === 1 ? "form" : "forms"}
                      </span>
                    </div>
                  ))}
              </div>
            </div>
            <div data-testid="insights-assistant-gaps">
              <div className="text-xs uppercase tracking-wider font-semibold text-muted-foreground mb-2">
                Gaps — asked, nothing matched {data.assistant_gaps.length > 0 && <span className="text-amber-600">({data.assistant_gaps.length})</span>}
              </div>
              {data.assistant_gaps.length === 0 ? (
                <p className="text-sm text-muted-foreground">Every question found a form. 🎯</p>
              ) : (
                <div className="space-y-1.5">
                  <p className="text-xs text-muted-foreground mb-1">Strong candidates for your next forms:</p>
                  {data.assistant_gaps.map((a, i) => (
                    <div key={i} className="text-sm bg-amber-50 border border-amber-200 px-3 py-1.5 text-amber-900">{a.query}</div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {data.assistant_captures_total > 0 && (
        <div className="mt-6 bg-white border border-border p-6" data-testid="insights-finder-leads">
          <div className="flex items-center gap-2 mb-1">
            <Mail className="w-4 h-4 text-primary" />
            <h3 className="font-heading font-bold text-sm">Form Finder leads — emailed themselves forms ({data.assistant_captures_total})</h3>
          </div>
          <p className="text-xs text-muted-foreground mb-5">High-intent visitors who described a real problem and gave you their email. Follow up.</p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
                  <th className="py-2 pr-4 font-semibold">Email</th>
                  <th className="py-2 pr-4 font-semibold">Their question</th>
                  <th className="py-2 pr-4 font-semibold whitespace-nowrap">Forms</th>
                  <th className="py-2 font-semibold whitespace-nowrap">When</th>
                </tr>
              </thead>
              <tbody>
                {data.assistant_captures.map((c, i) => (
                  <tr key={i} className="border-b border-border align-top" data-testid={`finder-lead-${i}`}>
                    <td className="py-2.5 pr-4 font-medium whitespace-nowrap">
                      <a href={`mailto:${c.emailed_to}`} className="text-primary hover:underline">{c.emailed_to}</a>
                    </td>
                    <td className="py-2.5 pr-4 text-muted-foreground">{c.query}</td>
                    <td className="py-2.5 pr-4 whitespace-nowrap">{c.result_count}</td>
                    <td className="py-2.5 text-muted-foreground whitespace-nowrap">{new Date(c.created_at).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      <p className="text-sm text-muted-foreground mb-6">What customers are searching for and buying — so you build what sells.</p>

      <div className="grid lg:grid-cols-3 gap-6">
        <Panel icon={Search} title={`Top searches (${data.total_searches} total)`} testid="insights-searches">
          {data.top_searches.length === 0 ? (
            <Empty>No searches logged yet.</Empty>
          ) : (
            data.top_searches.map((s, i) => (
              <Row key={i} label={s.term} value={s.count} max={data.top_searches[0].count} />
            ))
          )}
        </Panel>

        <Panel icon={FileText} title="Most-acquired forms" testid="insights-forms">
          {data.top_forms.length === 0 ? (
            <Empty>No form purchases yet.</Empty>
          ) : (
            data.top_forms.map((f, i) => (
              <Row key={i} label={f.title} value={f.count} max={data.top_forms[0].count} />
            ))
          )}
        </Panel>

        <Panel icon={Package} title="Top packages" testid="insights-packages">
          {data.top_packages.length === 0 ? (
            <Empty>No package sales yet.</Empty>
          ) : (
            data.top_packages.map((p, i) => (
              <div key={i} className="flex items-center justify-between py-2 border-b border-border last:border-0">
                <span className="text-sm truncate pr-2">{p.name}</span>
                <span className="text-sm font-semibold whitespace-nowrap">{p.count} · ${p.revenue.toLocaleString()}</span>
              </div>
            ))
          )}
        </Panel>

        <Panel icon={Eye} title={`Most-previewed forms (${(data.total_previews || 0).toLocaleString()} views)`} testid="insights-previews">
          {!data.top_previews || data.top_previews.length === 0 ? (
            <Empty>No form previews yet.</Empty>
          ) : (
            data.top_previews.map((p, i) => (
              <Row key={i} label={`${p.code ? p.code + " · " : ""}${p.title}`} value={p.count} max={data.top_previews[0].count} />
            ))
          )}
        </Panel>
      </div>

      <div className="mt-8 bg-primary/5 border border-primary/20 p-6 flex items-start gap-3">
        <TrendingUp className="w-5 h-5 text-primary shrink-0 mt-0.5" />
        <p className="text-sm text-foreground/80">
          Searches with no matching form = demand you haven't filled yet. <strong>Most-previewed forms</strong> show what prospects are most curious about — feature high-preview, low-purchase forms in packs and on the homepage to convert that interest into sales.
        </p>
      </div>
    </div>
  );
}

function Panel({ icon: Icon, title, children, testid }) {
  return (
    <div className="bg-white border border-border p-6" data-testid={testid}>
      <div className="flex items-center gap-2 mb-5">
        <Icon className="w-4 h-4 text-primary" />
        <h2 className="font-heading font-bold text-sm">{title}</h2>
      </div>
      <div className="space-y-1">{children}</div>
    </div>
  );
}

function Row({ label, value, max }) {
  const pct = max ? Math.max(8, Math.round((value / max) * 100)) : 0;
  return (
    <div className="py-1.5">
      <div className="flex items-center justify-between text-sm mb-1">
        <span className="truncate pr-2">{label}</span>
        <span className="font-semibold">{value}</span>
      </div>
      <div className="h-1.5 bg-secondary"><div className="h-full bg-primary" style={{ width: `${pct}%` }} /></div>
    </div>
  );
}

function Empty({ children }) {
  return <p className="text-sm text-muted-foreground py-4">{children}</p>;
}

const AUD_TILES = [
  [Users, "Anonymous visitors", "anonymous_visitors", "Browsed, never gave an email"],
  [UserPlus, "Registered users", "registered_users", "Created an account"],
  [Mail, "Email leads", "email_leads", "Gave email (forms / promos)"],
  [Gift, "Free claimants", "free_claimants", "Claimed their 3 free forms"],
  [ShoppingCart, "Checkout starters", "checkout_starters", "Started a paid checkout"],
  [CreditCard, "Paid customers", "paid_customers", "Completed a purchase"],
];

function Audience({ d }) {
  const a = d.audience || {};
  const dev = d.devices || {};
  const devTotal = Math.max(1, (dev.desktop || 0) + (dev.mobile || 0) + (dev.tablet || 0));
  const devRows = [
    [Monitor, "Desktop", dev.desktop || 0],
    [Smartphone, "Mobile", dev.mobile || 0],
    [Tablet, "Tablet", dev.tablet || 0],
  ];
  return (
    <div className="mt-8" data-testid="insights-audience">
      <h2 className="font-heading font-bold text-xl mb-1">Audience</h2>
      <p className="text-sm text-muted-foreground mb-6">Every visitor type counted separately — leads and registrations are never merged.</p>
      <div className="grid grid-cols-2 lg:grid-cols-6 gap-4">
        {AUD_TILES.map(([Icon, label, key, hint]) => (
          <div key={key} className="bg-white border border-border p-5" data-testid={`audience-${key}`}>
            <Icon className="w-5 h-5 text-primary mb-3" />
            <div className="font-heading font-black text-2xl">{(a[key] || 0).toLocaleString()}</div>
            <div className="text-xs font-semibold mt-1">{label}</div>
            <div className="text-[11px] text-muted-foreground mt-0.5 leading-tight">{hint}</div>
          </div>
        ))}
      </div>
      <div className="bg-white border border-border p-6 mt-4" data-testid="audience-devices">
        <h3 className="font-heading font-bold text-sm mb-4">Sessions by device ({d.total_sessions || 0} total)</h3>
        <div className="grid sm:grid-cols-3 gap-6">
          {devRows.map(([Icon, label, val]) => (
            <div key={label}>
              <div className="flex items-center justify-between text-sm mb-1">
                <span className="flex items-center gap-2"><Icon className="w-4 h-4 text-primary" />{label}</span>
                <span className="font-semibold">{val.toLocaleString()} · {Math.round((val / devTotal) * 100)}%</span>
              </div>
              <div className="h-1.5 bg-secondary"><div className="h-full bg-primary" style={{ width: `${Math.round((val / devTotal) * 100)}%` }} /></div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function EntryPages({ d }) {
  const pages = (d.pages || []).filter((p) => p.entrants > 0 || p.sessions > 0 || p.registrations > 0 || p.leads > 0 || p.purchases > 0);
  const pctOf = (n, dv) => (dv > 0 ? Math.round((n / dv) * 1000) / 10 : 0);
  return (
    <div className="mt-8" data-testid="insights-entry-pages">
      <div className="flex items-center gap-2 mb-1">
        <Compass className="w-5 h-5 text-primary" />
        <h2 className="font-heading font-bold text-xl">Entry pages</h2>
      </div>
      <p className="text-sm text-muted-foreground mb-6">
        Where visitors <strong>first land</strong> on the site (first-touch), across every public page — grouped by type.
        Conversions are credited to the page a visitor first entered on.
      </p>
      <div className="bg-white border border-border p-6" data-testid="entry-pages-table">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
                <th className="py-2 pr-4 font-semibold">Entry page</th>
                <th className="py-2 pr-4 font-semibold whitespace-nowrap">Entrants</th>
                <th className="py-2 pr-4 font-semibold whitespace-nowrap">Sessions</th>
                <th className="py-2 pr-4 font-semibold whitespace-nowrap">Registrations</th>
                <th className="py-2 pr-4 font-semibold whitespace-nowrap">Leads</th>
                <th className="py-2 pr-4 font-semibold whitespace-nowrap">Free claims</th>
                <th className="py-2 pr-4 font-semibold whitespace-nowrap">Checkouts</th>
                <th className="py-2 pr-4 font-semibold whitespace-nowrap">Purchases</th>
                <th className="py-2 font-semibold whitespace-nowrap">Conv. %</th>
              </tr>
            </thead>
            <tbody>
              {pages.length === 0 ? (
                <tr><td colSpan={9}><Empty>No entry data yet — it starts filling in as visitors arrive.</Empty></td></tr>
              ) : pages.map((p) => (
                <tr key={p.key} className="border-b border-border last:border-0" data-testid={`entry-row-${p.key}`}>
                  <td className="py-2.5 pr-4 font-medium">{p.label}</td>
                  <td className="py-2.5 pr-4">{p.entrants.toLocaleString()}</td>
                  <td className="py-2.5 pr-4">{p.sessions.toLocaleString()}</td>
                  <td className="py-2.5 pr-4">{p.registrations.toLocaleString()}</td>
                  <td className="py-2.5 pr-4">{p.leads.toLocaleString()}</td>
                  <td className="py-2.5 pr-4">{p.free_claims.toLocaleString()}</td>
                  <td className="py-2.5 pr-4">{p.checkout_starts.toLocaleString()}</td>
                  <td className="py-2.5 pr-4 font-semibold">{p.purchases.toLocaleString()}</td>
                  <td className="py-2.5"><span className="text-primary font-semibold">{pctOf(p.purchases, p.entrants)}%</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-xs text-muted-foreground mt-3">
          Conv. % = purchases ÷ entrants. Registrations, leads and purchases populate as new visitors convert — historical visits are counted as entrants but couldn't be tied to a person.
        </p>
      </div>
    </div>
  );
}

function CampaignCompare({ c }) {
  const key = (r) => `${r.source}||${r.campaign}`;
  const map = {};
  (c.lastWeek || []).forEach((r) => { map[key(r)] = { source: r.source, campaign: r.campaign, last: r, cur: null }; });
  (c.thisWeek || []).forEach((r) => {
    const k = key(r);
    map[k] = map[k] || { source: r.source, campaign: r.campaign, last: null, cur: null };
    map[k].cur = r;
  });
  const rows = Object.values(map).filter(
    (r) => (r.cur?.entrants || r.cur?.purchases || r.last?.entrants || r.last?.purchases)
  );
  rows.sort((a, b) => ((b.cur?.purchases || 0) + (b.cur?.entrants || 0)) - ((a.cur?.purchases || 0) + (a.cur?.entrants || 0)));
  const n = (v) => (v == null ? "—" : (v || 0).toLocaleString());
  const delta = (cur, last) => {
    const a = cur || 0, b = last || 0;
    if (a === b) return null;
    const up = a > b;
    return <span className={`ml-1 text-[10px] font-bold ${up ? "text-green-600" : "text-red-500"}`}>{up ? "▲" : "▼"}{Math.abs(a - b)}</span>;
  };

  return (
    <div className="mt-8" data-testid="insights-campaigns">
      <div className="flex items-center gap-2 mb-1">
        <Target className="w-5 h-5 text-primary" />
        <h2 className="font-heading font-bold text-xl">Campaign comparison — this week vs last</h2>
      </div>
      <p className="text-sm text-muted-foreground mb-6">
        Grouped by first-touch source &amp; campaign. Tag ad links with <code className="text-xs">?utm_source=facebook&amp;utm_campaign=fall</code> to see them broken out here.
      </p>
      <div className="bg-white border border-border p-6 overflow-x-auto" data-testid="campaigns-table">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
              <th className="py-2 pr-4 font-semibold" rowSpan={2}>Source / Campaign</th>
              <th className="py-2 px-3 font-semibold text-center border-l border-border" colSpan={3}>This week</th>
              <th className="py-2 px-3 font-semibold text-center border-l border-border" colSpan={3}>Last week</th>
            </tr>
            <tr className="text-left text-[10px] uppercase tracking-wider text-muted-foreground border-b border-border">
              <th className="py-1.5 px-3 font-semibold border-l border-border">Entrants</th>
              <th className="py-1.5 px-3 font-semibold">Leads</th>
              <th className="py-1.5 px-3 font-semibold">Buys</th>
              <th className="py-1.5 px-3 font-semibold border-l border-border">Entrants</th>
              <th className="py-1.5 px-3 font-semibold">Leads</th>
              <th className="py-1.5 px-3 font-semibold">Buys</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr><td colSpan={7}><Empty>No campaign traffic in the last two weeks yet. Tag your ad links to populate this.</Empty></td></tr>
            ) : rows.map((r, i) => (
              <tr key={i} className="border-b border-border last:border-0" data-testid={`campaign-row-${i}`}>
                <td className="py-2.5 pr-4 font-medium capitalize">
                  {r.source}{r.campaign ? <span className="text-muted-foreground font-normal"> · {r.campaign}</span> : ""}
                </td>
                <td className="py-2.5 px-3 border-l border-border">{n(r.cur?.entrants)}{r.cur && r.last && delta(r.cur.entrants, r.last.entrants)}</td>
                <td className="py-2.5 px-3">{n(r.cur?.leads)}</td>
                <td className="py-2.5 px-3 font-semibold">{n(r.cur?.purchases)}</td>
                <td className="py-2.5 px-3 border-l border-border text-muted-foreground">{n(r.last?.entrants)}</td>
                <td className="py-2.5 px-3 text-muted-foreground">{n(r.last?.leads)}</td>
                <td className="py-2.5 px-3 text-muted-foreground">{n(r.last?.purchases)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const OUTCOME_STYLE = {
  purchased: ["Purchased", "bg-green-100 text-green-800 border-green-200"],
  started_checkout: ["Started checkout", "bg-blue-100 text-blue-800 border-blue-200"],
  claimed_free: ["Claimed free forms", "bg-purple-100 text-purple-800 border-purple-200"],
  lead: ["Lead", "bg-amber-100 text-amber-800 border-amber-200"],
  registered: ["Registered", "bg-sky-100 text-sky-800 border-sky-200"],
  exited: ["Exited", "bg-gray-100 text-gray-600 border-gray-200"],
};

function Journeys({ d }) {
  const rows = d.journeys || [];
  return (
    <div className="mt-8" data-testid="insights-journeys">
      <div className="flex items-center gap-2 mb-1">
        <Route className="w-5 h-5 text-primary" />
        <h2 className="font-heading font-bold text-xl">Visitor journeys</h2>
      </div>
      <p className="text-sm text-muted-foreground mb-6">The most recent sessions — first entry through each page to exit or conversion.</p>
      <div className="bg-white border border-border divide-y divide-border" data-testid="journeys-list">
        {rows.length === 0 ? <div className="p-6"><Empty>No journeys recorded yet.</Empty></div> :
          rows.map((j, i) => {
            const [oLabel, oClass] = OUTCOME_STYLE[j.outcome] || OUTCOME_STYLE.exited;
            return (
              <div key={i} className="p-4" data-testid={`journey-${i}`}>
                <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <span className="font-mono">#{j.visitor_id}</span>
                    <span className="capitalize">· {j.device}</span>
                    <span>· {j.source}</span>
                    {j.utm_campaign && <span>· {j.utm_campaign}</span>}
                    {j.started_at && <span>· {new Date(j.started_at).toLocaleString()}</span>}
                  </div>
                  <span className={`text-xs font-semibold px-2 py-0.5 border ${oClass}`}>{oLabel}</span>
                </div>
                <div className="flex items-center gap-1 flex-wrap text-xs">
                  <DoorOpen className="w-3.5 h-3.5 text-primary shrink-0" />
                  {j.steps.map((s, k) => (
                    <span key={k} className="flex items-center gap-1">
                      <span className="bg-secondary px-1.5 py-0.5 font-mono">{s}</span>
                      {k < j.steps.length - 1 && <span className="text-muted-foreground">→</span>}
                    </span>
                  ))}
                  {j.step_count > j.steps.length && <span className="text-muted-foreground">+{j.step_count - j.steps.length} more</span>}
                </div>
              </div>
            );
          })}
      </div>
    </div>
  );
}

function Traffic({ t }) {
  const maxDay = Math.max(1, ...t.daily.map((d) => d.views));
  const tiles = [
    [Users, "Unique visitors", (t.unique_visitors || 0).toLocaleString()],
    [BarChart3, "Total page views", (t.total_views || 0).toLocaleString()],
    [TrendingUp, "Views today", (t.views_today || 0).toLocaleString()],
    [Globe, "Visitors (7 days)", (t.unique_7d || 0).toLocaleString()],
  ];
  return (
    <div className="mt-8" data-testid="insights-traffic">
      <h2 className="font-heading font-bold text-xl mb-1">Visitors</h2>
      <p className="text-sm text-muted-foreground mb-6">First-party traffic — counted on your site, no third party.</p>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {tiles.map(([Icon, label, val]) => (
          <div key={label} className="bg-white border border-border p-5">
            <Icon className="w-5 h-5 text-primary mb-3" />
            <div className="font-heading font-black text-2xl" data-testid={`traffic-${label.split(" ")[0].toLowerCase()}`}>{val}</div>
            <div className="text-xs text-muted-foreground mt-1">{label}</div>
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-3 gap-6 mt-6">
        <div className="bg-white border border-border p-6 lg:col-span-1" data-testid="traffic-chart">
          <h3 className="font-heading font-bold text-sm mb-5">Last 14 days</h3>
          <div className="flex items-end gap-1 h-28">
            {t.daily.map((d) => (
              <div key={d.day} className="flex-1 flex flex-col items-center justify-end group relative" title={`${d.day}: ${d.views} views, ${d.uniques} visitors`}>
                <div className="w-full bg-primary/80 hover:bg-primary transition-colors" style={{ height: `${Math.round((d.views / maxDay) * 100)}%`, minHeight: d.views ? 3 : 0 }} />
              </div>
            ))}
          </div>
          <div className="flex justify-between text-[10px] text-muted-foreground mt-2">
            <span>{t.daily[0]?.day.slice(5)}</span>
            <span>{t.daily[t.daily.length - 1]?.day.slice(5)}</span>
          </div>
        </div>

        <div className="bg-white border border-border p-6">
          <h3 className="font-heading font-bold text-sm mb-5">Top pages (30 days)</h3>
          <div className="space-y-1">
            {t.top_pages.length === 0 ? <Empty>No page views yet.</Empty> :
              t.top_pages.map((p, i) => <Row key={i} label={p.path} value={p.views} max={t.top_pages[0].views} />)}
          </div>
        </div>

        <div className="bg-white border border-border p-6">
          <h3 className="font-heading font-bold text-sm mb-5">Traffic sources</h3>
          <div className="space-y-1">
            {t.top_referrers.length === 0 ? <Empty>Direct traffic only so far.</Empty> :
              t.top_referrers.map((r, i) => <Row key={i} label={r.host} value={r.views} max={t.top_referrers[0].views} />)}
          </div>
        </div>
      </div>

      {t.funnel && <Funnel f={t.funnel} />}
    </div>
  );
}

function Funnel({ f }) {
  const stages = [
    ["Visitors", f.visitors, "Unique people who viewed the site"],
    ["Leads", f.leads, "Gave their email (free-forms / contact)"],
    ["Free claims", f.free_claims, "Claimed their 3 free forms"],
    ["Customers", f.customers, "Made a paid purchase"],
  ];
  const top = Math.max(1, f.visitors, f.leads, f.free_claims, f.customers);
  const pctOf = (n, d) => (d > 0 ? Math.round((n / d) * 1000) / 10 : 0);
  return (
    <div className="bg-white border border-border p-6 mt-6" data-testid="traffic-funnel">
      <h3 className="font-heading font-bold text-sm mb-1">Conversion funnel</h3>
      <p className="text-xs text-muted-foreground mb-5">How traffic turns into leads and sales (all-time).</p>
      <div className="space-y-3">
        {stages.map(([label, val, hint], i) => {
          const prev = i === 0 ? null : stages[i - 1][1];
          const stepRate = prev != null ? pctOf(val, prev) : null;
          return (
            <div key={label}>
              <div className="flex items-center justify-between text-sm mb-1">
                <span className="font-medium">{label} <span className="text-muted-foreground font-normal">· {hint}</span></span>
                <span className="font-semibold whitespace-nowrap">
                  {val.toLocaleString()}
                  {stepRate != null && <span className="text-xs text-primary ml-2 pl-2 border-l border-border">{stepRate}% from prev</span>}
                </span>
              </div>
              <div className="h-3 bg-secondary">
                <div className="h-full bg-primary" style={{ width: `${Math.max(2, Math.round((val / top) * 100))}%` }} />
              </div>
            </div>
          );
        })}
      </div>
      <div className="mt-4 text-xs text-muted-foreground border-t border-border pt-3">
        Visitor → customer conversion: <strong className="text-foreground">{pctOf(f.customers, f.visitors)}%</strong>
        {" · "}Visitor → lead: <strong className="text-foreground">{pctOf(f.leads, f.visitors)}%</strong>
      </div>
    </div>
  );
}

function LandingFunnel({ d }) {
  const pctOf = (n, dv) => (dv > 0 ? Math.round((n / dv) * 1000) / 10 : 0);
  const pages = d.pages || [];
  const sources = (d.by_source || []).filter((s) => s.visits > 0 || s.purchases > 0);
  const money = (n) => `$${(n || 0).toLocaleString()}`;
  return (
    <div className="mt-8" data-testid="insights-landing-funnel">
      <div className="flex items-center gap-2 mb-1">
        <Target className="w-5 h-5 text-primary" />
        <h2 className="font-heading font-bold text-xl">Landing page funnel</h2>
      </div>
      <p className="text-sm text-muted-foreground mb-6">
        How each buyer landing page (<code className="text-xs">/for/…</code>) turns ad clicks into pack sales. Add <code className="text-xs">?utm_source=facebook</code> to your ad links to attribute by source.
      </p>

      <div className="bg-white border border-border p-6" data-testid="landing-funnel-pages">
        <h3 className="font-heading font-bold text-sm mb-4">By page</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
                <th className="py-2 pr-4 font-semibold">Landing page</th>
                <th className="py-2 pr-4 font-semibold whitespace-nowrap">Visitors</th>
                <th className="py-2 pr-4 font-semibold whitespace-nowrap">Checkouts started</th>
                <th className="py-2 pr-4 font-semibold whitespace-nowrap">Purchases</th>
                <th className="py-2 pr-4 font-semibold whitespace-nowrap">Revenue</th>
                <th className="py-2 font-semibold whitespace-nowrap">Conv. rate</th>
              </tr>
            </thead>
            <tbody>
              {pages.map((p) => (
                <tr key={p.path} className="border-b border-border last:border-0" data-testid={`landing-row-${p.path}`}>
                  <td className="py-2.5 pr-4 font-medium whitespace-nowrap">{p.label} <span className="text-xs text-muted-foreground font-normal block">{p.path}</span></td>
                  <td className="py-2.5 pr-4">{p.visits.toLocaleString()}</td>
                  <td className="py-2.5 pr-4">{p.starts.toLocaleString()}</td>
                  <td className="py-2.5 pr-4 font-semibold">{p.purchases.toLocaleString()}</td>
                  <td className="py-2.5 pr-4 font-semibold">{money(p.revenue)}</td>
                  <td className="py-2.5"><span className="text-primary font-semibold">{pctOf(p.purchases, p.visits)}%</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-xs text-muted-foreground mt-3">Conversion rate = purchases ÷ visitors. Compare pages to see which toolkit resonates.</p>
      </div>

      <div className="bg-white border border-border p-6 mt-6" data-testid="landing-funnel-sources">
        <h3 className="font-heading font-bold text-sm mb-4">By ad source</h3>
        {sources.length === 0 ? (
          <Empty>No landing-page visits with a source yet. Tag your ad links with <code className="text-xs">?utm_source=</code>.</Empty>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
                  <th className="py-2 pr-4 font-semibold">Source</th>
                  <th className="py-2 pr-4 font-semibold whitespace-nowrap">Visitors</th>
                  <th className="py-2 pr-4 font-semibold whitespace-nowrap">Purchases</th>
                  <th className="py-2 pr-4 font-semibold whitespace-nowrap">Revenue</th>
                  <th className="py-2 font-semibold whitespace-nowrap">Conv. rate</th>
                </tr>
              </thead>
              <tbody>
                {sources.map((s) => (
                  <tr key={s.source} className="border-b border-border last:border-0" data-testid={`source-row-${s.source}`}>
                    <td className="py-2.5 pr-4 font-medium capitalize">{s.source}</td>
                    <td className="py-2.5 pr-4">{s.visits.toLocaleString()}</td>
                    <td className="py-2.5 pr-4 font-semibold">{s.purchases.toLocaleString()}</td>
                    <td className="py-2.5 pr-4 font-semibold">{money(s.revenue)}</td>
                    <td className="py-2.5"><span className="text-primary font-semibold">{pctOf(s.purchases, s.visits)}%</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
